import { useEffect, useState, useRef, RefObject } from 'react'

export function useScrollInView<T extends HTMLElement>(): [
  RefObject<T>,
  number,
] {
  const containerRef = useRef<T>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const children = Array.from(container.children)
    if (children.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        let maxVisibility = { index: -1, ratio: 0 }

        entries.forEach(entry => {
          const index = children.indexOf(entry.target)
          if (index !== -1 && entry.intersectionRatio > maxVisibility.ratio) {
            maxVisibility = { index, ratio: entry.intersectionRatio }
          }
        })

        if (maxVisibility.index !== -1 && maxVisibility.ratio > 0.1) {
          setActiveIndex(maxVisibility.index)
        }
      },
      {
        root: container,
        rootMargin: '0px -20% 0px -20%',
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      }
    )

    children.forEach(child => observer.observe(child))

    return () => {
      observer.disconnect()
    }
  }, [])

  return [containerRef, activeIndex]
}
